import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";

/**
 * Public, unauthenticated lookup behind the homepage Top 10 finder. Returns
 * published list data only. No credits are consumed and nothing is written.
 */

const inputSchema = z.union([
  z.object({
    kind: z.literal("company"),
    stage: z.string().trim().min(1).max(64),
    category: z.string().trim().min(1).max(200),
  }),
  z.object({
    kind: z.literal("provider"),
    groupKey: z.string().trim().min(1).max(64),
    categoryKey: z.string().trim().min(1).max(200),
  }),
]);

export const lookupPublicRanking = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => inputSchema.parse(input))
  .handler(async ({ data }) => {
    const {
      clientIpFrom,
      consumeLookup,
      isKnownProviderCategory,
      readCompanyList,
      readProviderList,
    } = await import("@/server/public-rankings.server");

    const ip = clientIpFrom(getRequest().headers);
    if (!consumeLookup(ip)) {
      return { ok: false as const, reason: "rate_limited" as const, list: null };
    }

    if (data.kind === "company") {
      return { ok: true as const, reason: null, list: readCompanyList(data.stage, data.category) };
    }

    if (!isKnownProviderCategory(data.groupKey, data.categoryKey)) {
      return { ok: true as const, reason: null, list: null };
    }
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const list = await readProviderList(
      supabaseAdmin as never,
      data.groupKey,
      data.categoryKey,
    );
    return { ok: true as const, reason: null, list };
  });
